/**
 * Represents resources from the /memberships/ API collection.
 */
import type { ActivatedRoute } from '@angular/router';
import { loadBoolFilterFromGetParams, loadIntFilterFromGetParams, loadStringFilterFromGetParams } from '../reporting-core/utils';
import { EventTypeStatus } from './event-type-enums';
import {
  CustomerMembershipStatus,
  MembershipDiscountApplyMethod,
  MembershipDiscountType,
  MembershipGiftDiscountType,
  MembershipInterval, MembershipPurchaseType,
  MembershipStatus,
  MembershipVisibility
} from './membership-enums';
import { PaymentProcessorType } from './organization-config-enums';
import { PaymentMethodType, PaymentType } from './payment-enums';

export interface DateRangeOption {
  start_date: string;
  end_date: string;
}

export interface MembershipUpfrontPaymentOption {
  number_of_periods: number;
  discount_amount: number;
  discount_type: MembershipDiscountType;
  min_time_without_active_or_frozen_membership_days?: number;
  available_start_date?: string;  // ISO date; a missing key means no limit on that side.
  available_end_date?: string;
}

export interface GroupMembershipDiscount {
  number_in_tier: number;
  discount_amount: number;
}

export interface MembershipPerCustomerAddOn {
  id: string;
  name: string;
  fee: string;  // Must be a string containing a decimal number.
  is_visible: boolean;
  icon_color_hex: string;  // Must be a six-hex-digit color.
  icon_initials: string;
  revenue_account_codes: { [key: string]: string | null };
  tax_rate_id?: number;
  tax_rate_per_location?: { [key: string]: number };
  // Both flags are optional: add-ons saved before PRO-1430 carry neither key,
  // and an absent key is treated as false by the server.
  sends_allthenticate_invite?: boolean;
  overrides_limited_entry?: boolean;
}

export interface MembershipLocationSpecificPricingMatrix {
  location_ids: number[];

  // Individual (non-group) membership pricing fields
  membership_fee: string | null;
  freeze_fee?: string | null;

  // Group membership pricing fields
  discount_type: MembershipDiscountType;
  discounts_are_incremental: boolean;
  discounts_apply_to_individually_frozen_members: boolean;
  adult_base_fee: string | null;
  youth_base_fee: string | null;
  adult_freeze_fee?: string | null;
  youth_freeze_fee?: string | null;
  adult_discounts: GroupMembershipDiscount[];
  youth_discounts: GroupMembershipDiscount[];

  // Applies to both individual and group memberships
  initiation_fee: number;
  // Maps each add-on ID to its fee. If an add-on's ID is absent, or it maps
  // to null, then the add-on is not available at these locations.
  per_customer_add_on_fees: { [key: string]: string };
}

export interface Membership {
  id: number;
  url: string;
  name: string;
  purchase_type: MembershipPurchaseType;
  is_group: boolean;
  interval: MembershipInterval | '';
  has_fixed_date_ranges: boolean;
  duration_years: number | null;
  duration_months: number | null;
  duration_days: number | null;
  date_range_options: DateRangeOption[];
  upfront_payment_options: MembershipUpfrontPaymentOption[];
  upfront_payment_climber_app_text: string;
  status: MembershipStatus;
  description: string;
  confirmation_text: string;
  payment_information_text: string;
  recurring_payment_confirmation_text: string;
  failed_payment_notification_text: string;
  terms_and_conditions_checkbox_text: string;
  membership_management_info_alert_text: string;
  visibility: MembershipVisibility;
  is_new_members_only: boolean;
  max_benefit_grants_per_customer: number | null;
  is_single_location: boolean;
  is_location_restricted: boolean;
  allowed_locations: {id: number, name: string}[];
  num_new_customer_guest_passes: number;
  new_customer_guest_pass_frequency: MembershipInterval;
  num_lapsed_customer_guest_passes: number;
  lapsed_customer_guest_pass_frequency: MembershipInterval;
  num_all_customer_guest_passes: number;
  all_customer_guest_pass_frequency: MembershipInterval;
  voucher_type_id: number | null;
  voucher_type_name: string | null;
  voucher_type_status: EventTypeStatus | null;
  voucher_category_id: number | null;
  voucher_category_name: string | null;
  num_vouchers?: number;

  // Policy fields
  allow_freezing: boolean;
  allow_individual_freezing: boolean;
  freeze_wait_period_weeks?: number;
  freeze_wait_period_months?: number;
  freeze_notice_period_days?: number;
  freeze_notice_period_months?: number;
  maximum_freeze_time_weeks?: number;
  maximum_freeze_time_months?: number;
  minimum_freeze_time_weeks?: number;
  minimum_freeze_time_months?: number;
  cancel_membership_when_maximum_freeze_reached: boolean;
  allow_cancellation: boolean;
  cancellation_wait_period_weeks?: number;
  cancellation_wait_period_months?: number;
  cancellation_notice_period_days?: number;
  cancellation_notice_period_months?: number;
  cancellation_date_range_start?: string | null;
  cancellation_date_range_end?: string | null;
  allow_selecting_start_date: boolean;
  bill_day_of_month?: number;
  has_limited_entry: boolean;
  sends_allthenticate_invite: boolean;

  // Individual (non-group) membership pricing fields
  membership_fee: number | null;
  freeze_fee: number | null;

  // Group membership pricing fields
  has_youth_tier: boolean;
  has_fixed_freeze_fee: boolean;
  discount_type: MembershipDiscountType;
  discounts_are_incremental: boolean;
  discounts_apply_to_individually_frozen_members: boolean;
  adult_tier_name: string;
  youth_tier_name: string;
  adult_min_age: number;
  adult_base_fee: number | null;
  youth_base_fee: number | null;
  adult_freeze_fee: number | null;
  youth_freeze_fee: number | null;
  adult_discounts: GroupMembershipDiscount[];
  youth_discounts: GroupMembershipDiscount[];
  allow_unlimited_adults: boolean;
  allow_unlimited_youths: boolean;
  minimum_num_members: number;
  minimum_num_adults: number;
  minimum_num_youths: number;

  // Applies to both individual and group memberships
  initiation_fee: number;
  location_specific_pricing_matrices: MembershipLocationSpecificPricingMatrix[];

  tax_rate_id: number;
  tax_rate_name: string;
  tax_rate_initiation_fee_id: number;
  tax_rate_initiation_fee_name: string;
  tax_rate_per_location: { [key: string]: number };
  tax_rate_initiation_fee_per_location: { [key: string]: number };
  revenue_account_codes: { [key: string]: string | null };
  per_customer_add_ons: MembershipPerCustomerAddOn[];
  icon_color_hex: string;
  icon_initials: string;
  allow_purchasing_for_unlinked_customers: boolean;
  allow_purchase_without_login: boolean;
  require_staff_approval: boolean;
  is_youth_programming: boolean;
  rolling_event_type_id: number | null;
  mailchimp_scheduled_to_cancel_event_name: string;
  mailchimp_cancelled_event_name: string;
  mailchimp_purchased_by_customer_event_name: string;
  mailchimp_purchased_for_customer_event_name: string;
  mailchimp_active_member_tag_name: string;
  mailchimp_active_membership_owner_tag_name: string;
  member_square_customer_group_id: number | null;
  member_square_customer_group_name: string | null;
  member_lightspeed_customer_group_id: number | null;
  member_lightspeed_customer_group_name: string | null;
  order_number: number;

  created_at: string;
  updated_at: string;
  last_updated_by_id: number | null;
  last_updated_by_first_name: string | null;
  last_updated_by_last_name: string | null;
  last_updated_by_email: string | null;
  entry_time_slots_url: string;
}

export interface MembershipEntryTimeSlot {
  id: number;
  url: string;
  start_time: string;
  end_time: string;
  is_available_sunday: boolean;
  is_available_monday: boolean;
  is_available_tuesday: boolean;
  is_available_wednesday: boolean;
  is_available_thursday: boolean;
  is_available_friday: boolean;
  is_available_saturday: boolean;
  created_at: string;
  updated_at: string;
}

export interface MembershipDiscount {
  id: number;
  url: string;
  discount_apply_method: MembershipDiscountApplyMethod;
  code: string;
  name: string;
  discount_type: MembershipDiscountType;
  amount: number;
  is_recurring: boolean;
  applies_to_add_ons: boolean;
  applies_to_initiation_fees: boolean;
  waives_initiation_fees: boolean;
  start_date?: string;
  end_date?: string;
  membership_ids: number[];
  membership_names: string[];
  roster_ids: number[];
  roster_names: string[];
  requires_association: boolean;
  requires_all_associations: boolean;
  association_ids: number[];
  association_names: string[];
  requires_membership: boolean;
  required_membership_ids: number[];
  required_membership_names: string[];
  max_number_of_uses_per_customer: number | null;
  required_no_check_in_period_days: number | null;
  applies_to_first_time_members_only: boolean;
  min_check_in_count: number | null;
  max_check_in_count: number | null;
  min_number_of_days_without_membership: number | null;
  min_age: number | null;
  max_age: number | null;
  required_active_duration_days: number | null;
  can_be_used_with_upfront_payments: boolean;
  referrer_credit_amount: string;
  max_number_of_referrer_credits_per_month: number | null;
  referrer_credit_only_applies_to_recurring_payments: boolean;
  referrer_must_have_active_membership: boolean;
  may_combine_with_other_discounts: boolean;
  customer_message: string;
  minimum_duration_before_offer_days: number | null;
  minimum_duration_before_offer_months: number | null;
  purchase_alert_message: string;
  purchase_terms_and_conditions: string;
  change_payment_method_alert_message: string;
  change_payment_method_terms_and_conditions: string;
  created_at: string;
  updated_at: string;
  last_updated_by_id: number | null;
  last_updated_by_first_name: string | null;
  last_updated_by_last_name: string | null;
  last_updated_by_email: string | null;
}

export interface MembershipGiftingConfig {
  id: number;
  url: string;
  assigned_membership_ids: number[];
  assigned_membership_names: string[];
  max_future_delivery_date_days: number;
  expiration_days: number;
  gift_giver_terms_and_conditions: string;
  gift_recipient_terms_and_conditions: string;
  created_at: string;
  updated_at: string;
}

export interface MembershipGiftDiscount {
  id: number;
  url: string;
  is_automatic: boolean;
  code: string;
  name: string;
  discount_type: MembershipGiftDiscountType;
  amount: number;
  start_date?: string;
  end_date?: string;
  membership_ids: number[];
  membership_names: string[];
  created_at: string;
  updated_at: string;
  last_updated_by_id: number | null;
  last_updated_by_first_name: string | null;
  last_updated_by_last_name: string | null;
  last_updated_by_email: string | null;
}

export interface CustomerMembershipCustomer {
  id: number;
  member_id: number;
  is_individually_frozen: boolean;
  is_pending_addition: boolean;
  is_pending_removal: boolean;
  member_freeze_date: string | null;
  member_unfreeze_date: string | null;
  first_name: string;
  last_name: string;
}

export interface CustomerMembershipUnlinkedCustomer {
  id: number;
  first_name: string;
  last_name: string;
}

// Why no terms & conditions were agreed to when a membership was purchased.
// Mirrors the TS_AND_CS_REASON_* constants on the backend serializer.
export enum MembershipTermsAndConditionsReason {
  migrated = 'migrated',
  benefit = 'benefit',
  noneConfigured = 'none_configured',
}

export interface MembershipTermsAndConditionsAgreed {
  // The terms & conditions text agreed to at purchase. Empty when agreed=false.
  text: string;
  agreed: boolean;
  // Set only when agreed=false, explaining why no T&Cs apply.
  reason: MembershipTermsAndConditionsReason | null;
}

export interface CustomerMembership {
  id: number;
  url: string;
  membership_id: number;
  membership_url: string;
  membership_status: MembershipStatus;
  name: string;
  purchase_type: MembershipPurchaseType;
  is_group: boolean;
  status: CustomerMembershipStatus;
  is_pending_staff_approval: boolean;
  interval: MembershipInterval | '';
  // null when the product has no fixed bill day.
  bill_day_of_month: number | null;
  has_fixed_date_ranges: boolean;
  duration_years: number | null;
  duration_months: number | null;
  duration_days: number | null;
  is_youth_programming: boolean;
  start_date: string;
  has_started: boolean;
  end_date: string;
  last_active_date: string | null;
  days_remaining: number;
  membership_cancel_date: string | null;
  membership_freeze_date: string | null;
  membership_unfreeze_date: string | null;
  new_end_date_if_unfrozen: string;
  last_date_frozen: string | null;
  days_frozen: number | null;
  last_invoice_id: number | null;
  last_invoice_amount: string | null;
  last_invoice_tax_amount: string | null;
  last_invoice_date: string | null;
  // The payment type on the last invoice that stops Capitan refunding it (a
  // POS or offline tender), or null when it can be refunded from here.
  last_invoice_refund_blocking_payment_type: PaymentType | null;
  first_completed_invoice_date: string | null;
  total_paid_to_date: string;
  total_refunded_to_date: string;
  purchase_completed_at: string | null;
  terms_and_conditions_agreed: MembershipTermsAndConditionsAgreed;
  owner_id: number;
  owner_url: string;
  owner_first_name: string;
  owner_last_name: string;
  owner_birthday: string;
  owner_email: string;
  owner_home_location_id: number | null;
  owner_home_location_name: string | null;
  owner_is_member: boolean;
  all_customers: CustomerMembershipCustomer[];
  customers_less_owner: CustomerMembershipCustomer[];
  unlinked_customers: CustomerMembershipUnlinkedCustomer[];
  customer_add_ons: { [key: string]: MembershipPerCustomerAddOn[] };
  customer_add_ons_to_add: { [key: string]: MembershipPerCustomerAddOn[] };
  customer_add_ons_to_remove: { [key: string]: MembershipPerCustomerAddOn[] };
  customer_add_on_options: MembershipPerCustomerAddOn[];
  billing_location_id: number | null;
  billing_location_url: string | null;
  billing_location_name: string | null;
  location_id?: number;
  location_url?: string;
  location_name?: string;
  payment_processor_connection_id: number | null;
  payment_processor_type: PaymentProcessorType | null;
  has_payment_method: boolean;
  payment_method_type: PaymentMethodType | null;
  payment_method_last4: string | null;
  payment_method_exp_month: number | null;
  payment_method_exp_year: number | null;
  one_off_discount_type: MembershipDiscountType;
  one_off_discount_amount: string;
  one_off_discount_applies_to_add_ons: boolean;
  one_off_discount_name: string;
  number_of_upfront_periods_paid: number | null;
  upfront_purchase_count: number;
  last_upfront_purchase_at: string | null;
  membership_has_upfront_payment_options: boolean;
  upcoming_bill_dates: string[];
  membership_cancellation_date_range_start: string | null;
  membership_cancellation_date_range_end: string | null;
  has_freeze_fee: boolean;
  // Whether a renewal or freeze fee has ever been billed. Once it has, the
  // start date can no longer be changed (PRO-1504).
  has_been_renewed: boolean;
  referring_customer_id: number | null;
  referring_customer_url: string | null;
  referring_customer_first_name: string | null;
  referring_customer_last_name: string | null;
  referring_customer_birthday: string | null;
  referring_customer_email: string | null;
  rolling_event_type_id: number | null;
  rolling_event_type_roster_id: number | null;
  rolling_event_type_roster_name: string | null;
  event_granting_benefit_id: number | null;
  event_granting_benefit_name: string | null;
  event_granting_benefit_first_start_datetime: string | null;
  event_granting_benefit_reservation_id: number | null;
  staff_user_id: number | null;
  staff_user_email: string | null;
  staff_user_first_name: string | null;
  staff_user_last_name: string | null;
  status_change_logs_url: string;
  created_at: string;
  updated_at: string;
  billing_amount: string;
  recurring_discount_is_automatic: boolean | null;
  recurring_discount_type: MembershipDiscountType | null;
  recurring_discount_amount: string | null;
  recurring_discount_name: string | null;
  recurring_discount_code: string | null;
  recurring_discount_association_id: number | null;
  recurring_discount_association_name: string | null;
  recurring_discount_membership_id: number | null;
  recurring_discount_membership_name: string | null;
  recurring_discount_applies_to_add_ons: boolean;
}

export interface CustomerMembershipCancellationLog {
  id: number;
  url: string;
  reason: string;
  customer_membership_id: number;
  billing_location_id: number | null;
  billing_location_name: string | null;
  location_id: number | null;
  location_name: number | null;
  membership_id: number;
  membership_name: string;
  owner_id: number;
  owner_first_name: string;
  owner_last_name: string;
  owner_birthday: string;
  owner_email: string | null;
  requested_by_user_id: number | null;
  requested_by_user_first_name: string | null;
  requested_by_user_last_name: string | null;
  requested_by_user_email: string | null;
  requested_by_customer_id: number | null;
  requested_by_customer_first_name: string | null;
  requested_by_customer_last_name: string | null;
  requested_by_customer_email: string | null;
  last_check_in_at_request: string | null;
  days_since_last_check_in_at_request: number | null;
  created_at: string;
  updated_at: string;
}

export interface CustomerMembershipFreezeLog {
  id: number;
  url: string;
  reason: string;
  customer_membership_id: number;
  billing_location_id: number | null;
  billing_location_name: string | null;
  location_id: number | null;
  location_name: number | null;
  membership_id: number;
  membership_name: string;
  owner_id: number;
  owner_first_name: string;
  owner_last_name: string;
  owner_birthday: string;
  owner_email: string | null;
  requested_by_user_id: number | null;
  requested_by_user_first_name: string | null;
  requested_by_user_last_name: string | null;
  requested_by_user_email: string | null;
  requested_by_customer_id: number | null;
  requested_by_customer_first_name: string | null;
  requested_by_customer_last_name: string | null;
  requested_by_customer_email: string | null;
  created_at: string;
  updated_at: string;
}

export interface Member {
  customer_id: number;
  customer_url: string;
  first_name: string;
  last_name: string;
  birthday: string;
  email: string | null;
  address_postcode: string;
  address_street_1: string;
  address_street_2: string;
  address_city: string;
  address_state: string;
  address_country: string;
  telephone: string;
  home_location_id: number | null;
  home_location_name: string | null;
  latest_check_in_timestamp: string | null;
  days_since_latest_check_in: number | null;
  add_ons: MembershipPerCustomerAddOn[];
  membership_id: number;
  membership_url: string;
  membership_type_id: number;
  membership_type_url: string;
  membership_name: string;
  membership_purchase_type: MembershipPurchaseType;
  membership_is_group: boolean;
  membership_is_youth_programming: boolean;
  membership_interval: MembershipInterval | '';
  membership_duration_years: number | null;
  membership_duration_months: number | null;
  membership_duration_days: number | null;
  location_id: number | null;
  location_url: string | null;
  location_name: string | null;
  owner_id: number;
  owner_url: string;
  owner_first_name: string;
  owner_last_name: string;
  owner_birthday: string;
  owner_email: string | null;
  owner_telephone: string;
  status: CustomerMembershipStatus;
  is_pending_staff_approval: boolean;
  start_date: string;
  end_date: string;
  membership_num_members?: number;
  last_invoice_id?: number | null;
  last_invoice_amount?: string | null;
  last_invoice_tax_amount?: string | null;
  last_invoice_date?: string | null;
  billing_amount?: string | null;
  billing_amount_per_member?: string | null;
  has_payment_method: boolean;
  number_of_upfront_periods_paid: number | null;
}

export interface CustomerMembershipReferral {
  id: number;
  url: string;
  membership_id: number;
  membership_url: string;
  membership_status: MembershipStatus;
  name: string;
  status: CustomerMembershipStatus;
  referring_customer_id: number | null;
  referring_customer_url: string | null;
  referring_customer_first_name: string | null;
  referring_customer_last_name: string | null;
  referring_customer_birthday: string | null;
  referring_customer_email: string | null;
  referring_customer_home_location_id: number | null;
  referring_customer_home_location_name: string | null;
  owner_id: number;
  owner_url: string;
  owner_first_name: string;
  owner_last_name: string;
  owner_birthday: string;
  owner_email: string;
  owner_home_location_id: number | null;
  owner_home_location_name: string | null;
  billing_location_id: number | null;
  billing_location_name: string | null;
  purchase_completed_at: string;
  created_at: string;
  updated_at: string;
}

export interface MembershipCategoryAssignedMembership {
  membership_id: number;
  membership_name: string;
  created_at: string;
  updated_at: string;
}

export interface MembershipCategory {
  id: number;
  url: string;
  name: string;
  url_hash: string;
  order_number?: number;
  assigned_memberships: MembershipCategoryAssignedMembership[];
  assigned_membership_ids?: number[];
  created_at: string;
  updated_at: string;
}

export interface MembershipFilters {
  status?: MembershipStatus;
  purchase_type?: MembershipPurchaseType;
  include_rolling?: boolean;
}

export interface MembershipDiscountFilters {
  search?: string;
  association_id?: number;
}

export interface MembershipGiftDiscountFilters {
  search?: string;
}

export interface CustomerMembershipFilters {
  start_date_min?: string;
  start_date_max?: string;
  start_date_range?: string;
  end_date_min?: string;
  end_date_max?: string;
  end_date_range?: string;
  membership_id?: number;
  membership_purchase_type?: MembershipPurchaseType;
  membership_is_group?: boolean;
  membership_is_youth_programming?: boolean;
  status_in?: string;
  owner_home_location_id?: number;
  billing_location_ids?: string;
  has_payment_method?: boolean;
  set_to_end?: boolean;
  set_to_start_in_future?: boolean;
  paid_upfront?: boolean;
  purchased_upfront_while_active?: boolean;
  number_of_upfront_periods_paid_min?: number;
  number_of_upfront_periods_paid_max?: number;
}

export interface CustomerMembershipCancellationLogFilters {
  created_at_min?: string;
  created_at_max?: string;
  created_at_range?: string;
  billing_location_ids?: string;
}

export function loadCustomerMembershipCancellationLogFiltersFromGetParams(
  activatedRoute: ActivatedRoute
): CustomerMembershipCancellationLogFilters {
  const filters: CustomerMembershipCancellationLogFilters = {};

  const initialQPs = activatedRoute.snapshot.queryParamMap;

  loadStringFilterFromGetParams(filters, 'created_at_min', initialQPs);
  loadStringFilterFromGetParams(filters, 'created_at_max', initialQPs);
  loadStringFilterFromGetParams(filters, 'created_at_range', initialQPs);
  loadStringFilterFromGetParams(filters, 'billing_location_ids', initialQPs);

  return filters;
}

export interface CustomerMembershipFreezeLogFilters {
  created_at_min?: string;
  created_at_max?: string;
  created_at_range?: string;
  billing_location_ids?: string;
}

export function loadCustomerMembershipFreezeLogFiltersFromGetParams(
  activatedRoute: ActivatedRoute
): CustomerMembershipFreezeLogFilters {
  const filters: CustomerMembershipFreezeLogFilters = {};

  const initialQPs = activatedRoute.snapshot.queryParamMap;

  loadStringFilterFromGetParams(filters, 'created_at_min', initialQPs);
  loadStringFilterFromGetParams(filters, 'created_at_max', initialQPs);
  loadStringFilterFromGetParams(filters, 'created_at_range', initialQPs);
  loadStringFilterFromGetParams(filters, 'billing_location_ids', initialQPs);

  return filters;
}

export interface MembershipLogReasonCount {
  reason: string;
  count: number;
}

export interface MemberFilters {
  start_date_min?: string;
  start_date_max?: string;
  start_date_range?: string;
  status_in?: string;
  purchase_type?: MembershipPurchaseType;
  is_group?: boolean;
  is_youth_programming?: boolean;
  membership_id?: number;
  add_on_name?: string;
  home_location_id?: number;
  has_payment_method?: boolean;
  set_to_end?: boolean;
  set_to_start_in_future?: boolean;
  latest_check_in_timestamp_min?: string;
  latest_check_in_timestamp_max?: string;
  latest_check_in_timestamp_range?: string;
  latest_check_in_timestamp_days_min?: number;
  latest_check_in_timestamp_days_max?: number;
}

export interface RosterMember {
  customer_id: number;
  customer_url: string;
  first_name: string;
  last_name: string;
  birthday: string;
  age: number;
  email: string | null;
  telephone: string;
  home_location_id: number | null;
  home_location_name: string | null;
  add_ons: MembershipPerCustomerAddOn[];
  membership_id: number;
  membership_url: string;
  membership_type_id: number;
  membership_type_url: string;
  membership_name: string;
  event_type_id: number;
  event_type_name: string;
  roster_id: number;
  roster_name: string;
  roster_location_id: number;
  roster_location_name: string;
  owner_id: number;
  owner_url: string;
  owner_first_name: string;
  owner_last_name: string;
  owner_email: string | null;
  owner_telephone: string;
  status: CustomerMembershipStatus;
  is_pending_staff_approval: boolean;
  start_date: string;
  end_date: string;
  has_started: boolean;
  membership_cancel_date: string | null;
  membership_freeze_date: string | null;
  membership_unfreeze_date: string | null;
  check_in_count: number;
  event_count: number;
}

export interface RosterMemberFilters {
  date_range_min?: string;
  date_range_max?: string;
  date_range_range?: string;
  event_type_id?: number;
  location_id?: number;
  roster_id?: number;
  check_in_count_min?: number;
  check_in_count_max?: number;
  status_in?: string;
  set_to_end?: boolean;
  set_to_start_in_future?: boolean;
}

export interface CustomerMembershipReferralFilters {
  purchase_date_min?: string;
  purchase_date_max?: string;
  purchase_date_range?: string;
  status_in?: string;
  owner_home_location_id?: number;
  billing_location_ids?: string;
}

export function loadMembershipDiscountFiltersFromGetParams(activatedRoute: ActivatedRoute): MembershipDiscountFilters {
  const filters: MembershipDiscountFilters = {};

  const initialQPs = activatedRoute.snapshot.queryParamMap;

  loadStringFilterFromGetParams(filters, 'search', initialQPs);

  return filters;
}

export function loadCustomerMembershipFiltersFromGetParams(activatedRoute: ActivatedRoute): CustomerMembershipFilters {
  const filters: CustomerMembershipFilters = {};

  const initialQPs = activatedRoute.snapshot.queryParamMap;

  loadStringFilterFromGetParams(filters, 'start_date_min', initialQPs);
  loadStringFilterFromGetParams(filters, 'start_date_max', initialQPs);
  loadStringFilterFromGetParams(filters, 'start_date_range', initialQPs);
  loadStringFilterFromGetParams(filters, 'end_date_min', initialQPs);
  loadStringFilterFromGetParams(filters, 'end_date_max', initialQPs);
  loadStringFilterFromGetParams(filters, 'end_date_range', initialQPs);
  loadIntFilterFromGetParams(filters, 'membership_id', initialQPs);
  loadStringFilterFromGetParams(filters, 'membership_purchase_type', initialQPs);
  loadBoolFilterFromGetParams(filters, 'membership_is_group', initialQPs);
  loadBoolFilterFromGetParams(filters, 'membership_is_youth_programming', initialQPs);
  loadStringFilterFromGetParams(filters, 'status_in', initialQPs);
  loadIntFilterFromGetParams(filters, 'owner_home_location_id', initialQPs);
  loadStringFilterFromGetParams(filters, 'billing_location_ids', initialQPs);
  loadBoolFilterFromGetParams(filters, 'has_payment_method', initialQPs);
  loadBoolFilterFromGetParams(filters, 'set_to_end', initialQPs);
  loadBoolFilterFromGetParams(filters, 'set_to_start_in_future', initialQPs);
  loadBoolFilterFromGetParams(filters, 'paid_upfront', initialQPs);
  loadBoolFilterFromGetParams(filters, 'purchased_upfront_while_active', initialQPs);
  loadIntFilterFromGetParams(filters, 'number_of_upfront_periods_paid_min', initialQPs);
  loadIntFilterFromGetParams(filters, 'number_of_upfront_periods_paid_max', initialQPs);

  return filters;
}

export function loadMemberFiltersFromGetParams(activatedRoute: ActivatedRoute): MemberFilters {
  const filters: MemberFilters = {};

  const initialQPs = activatedRoute.snapshot.queryParamMap;

  loadStringFilterFromGetParams(filters, 'start_date_min', initialQPs);
  loadStringFilterFromGetParams(filters, 'start_date_max', initialQPs);
  loadStringFilterFromGetParams(filters, 'start_date_range', initialQPs);
  loadStringFilterFromGetParams(filters, 'status_in', initialQPs);
  loadStringFilterFromGetParams(filters, 'purchase_type', initialQPs);
  loadBoolFilterFromGetParams(filters, 'is_group', initialQPs);
  loadBoolFilterFromGetParams(filters, 'is_youth_programming', initialQPs);
  loadIntFilterFromGetParams(filters, 'membership_id', initialQPs);
  loadStringFilterFromGetParams(filters, 'add_on_name', initialQPs);
  loadIntFilterFromGetParams(filters, 'home_location_id', initialQPs);
  loadBoolFilterFromGetParams(filters, 'has_payment_method', initialQPs);
  loadBoolFilterFromGetParams(filters, 'set_to_end', initialQPs);
  loadBoolFilterFromGetParams(filters, 'set_to_start_in_future', initialQPs);
  loadStringFilterFromGetParams(filters, 'latest_check_in_timestamp_min', initialQPs);
  loadStringFilterFromGetParams(filters, 'latest_check_in_timestamp_max', initialQPs);
  loadStringFilterFromGetParams(filters, 'latest_check_in_timestamp_range', initialQPs);
  loadIntFilterFromGetParams(filters, 'latest_check_in_timestamp_days_min', initialQPs);
  loadIntFilterFromGetParams(filters, 'latest_check_in_timestamp_days_max', initialQPs);

  return filters;
}

export function loadRosterMemberFiltersFromGetParams(activatedRoute: ActivatedRoute): RosterMemberFilters {
  const filters: RosterMemberFilters = {};

  const initialQPs = activatedRoute.snapshot.queryParamMap;

  loadStringFilterFromGetParams(filters, 'date_range_min', initialQPs);
  loadStringFilterFromGetParams(filters, 'date_range_max', initialQPs);
  loadStringFilterFromGetParams(filters, 'date_range_range', initialQPs);
  loadIntFilterFromGetParams(filters, 'event_type_id', initialQPs);
  loadIntFilterFromGetParams(filters, 'location_id', initialQPs);
  loadIntFilterFromGetParams(filters, 'roster_id', initialQPs);
  loadIntFilterFromGetParams(filters, 'check_in_count_min', initialQPs);
  loadIntFilterFromGetParams(filters, 'check_in_count_max', initialQPs);
  loadStringFilterFromGetParams(filters, 'status_in', initialQPs);
  loadBoolFilterFromGetParams(filters, 'set_to_end', initialQPs);
  loadBoolFilterFromGetParams(filters, 'set_to_start_in_future', initialQPs);

  return filters;
}

export function loadCustomerMembershipReferralFiltersFromGetParams(activatedRoute: ActivatedRoute): CustomerMembershipReferralFilters {
  const filters: CustomerMembershipReferralFilters = {};

  const initialQPs = activatedRoute.snapshot.queryParamMap;

  loadStringFilterFromGetParams(filters, 'purchase_date_min', initialQPs);
  loadStringFilterFromGetParams(filters, 'purchase_date_max', initialQPs);
  loadStringFilterFromGetParams(filters, 'purchase_date_range', initialQPs);
  loadStringFilterFromGetParams(filters, 'status_in', initialQPs);
  loadIntFilterFromGetParams(filters, 'owner_home_location_id', initialQPs);
  loadStringFilterFromGetParams(filters, 'billing_location_ids', initialQPs);

  return filters;
}

export function getMembershipPurchaseTypeDisplay(purchaseType: MembershipPurchaseType): string {
  switch (purchaseType) {
    case MembershipPurchaseType.recurring:
      return 'Recurring';
    case MembershipPurchaseType.prepaid:
      return 'Prepaid';
    case MembershipPurchaseType.benefit:
      return 'Benefit';
    default:
      return 'Unknown';
  }
}

/**
 * True for the billing intervals shorter than a month, which are the ones a
 * "billing day of month" setting cannot apply to. Takes the same
 * `MembershipInterval | ''` the Membership model stores, so templates can pass
 * `membership.interval` straight in.
 */
export function isSubMonthlyMembershipInterval(interval: MembershipInterval | ''): boolean {
  return [
    MembershipInterval.daily,
    MembershipInterval.weekly,
    MembershipInterval.biweekly,
    MembershipInterval.fourWeeks,
  ].includes(interval as MembershipInterval);
}

export function getMembershipIntervalDisplay(interval: MembershipInterval | ''): string {
  if (interval === MembershipInterval.daily) {
    return 'Daily';
  } else if (interval === MembershipInterval.weekly) {
    return 'Weekly';
  } else if (interval === MembershipInterval.biweekly) {
    return 'Biweekly';
  } else if (interval === MembershipInterval.fourWeeks) {
    return 'Every Four Weeks';
  } else if (interval === MembershipInterval.monthly) {
    return 'Monthly';
  } else if (interval === MembershipInterval.quarterly) {
    return 'Quarterly';
  } else if (interval === MembershipInterval.halfYearly) {
    return 'Semiannual';
  } else if (interval === MembershipInterval.yearly) {
    return 'Annual';
  } else {
    return 'Unknown';
  }
}

export function getMembershipIntervalDisplaySingle(interval: MembershipInterval | ''): string {
  if (interval === MembershipInterval.daily) {
    return 'day';
  } else if (interval === MembershipInterval.weekly) {
    return 'week';
  } else if (interval === MembershipInterval.biweekly) {
    return '2 weeks';
  } else if (interval === MembershipInterval.fourWeeks) {
    return '4 weeks';
  } else if (interval === MembershipInterval.monthly) {
    return 'month';
  } else if (interval === MembershipInterval.quarterly) {
    return 'quarter';
  } else if (interval === MembershipInterval.halfYearly) {
    return 'half-year';
  } else if (interval === MembershipInterval.yearly) {
    return 'year';
  } else {
    return 'unknown';
  }
}

export function getMembershipDurationDisplay(durationYears: number | null,
                                             durationMonths: number | null,
                                             durationDays: number | null): string {
  const durationParts = [];
  if (durationYears) {
    let durationPart = durationYears.toString() + ' Year';
    if (durationYears > 1) { durationPart += 's'; }
    durationParts.push(durationPart);
  }
  if (durationMonths) {
    let durationPart = durationMonths.toString() + ' Month';
    if (durationMonths > 1) { durationPart += 's'; }
    durationParts.push(durationPart);
  }
  if (durationDays) {
    let durationPart = durationDays.toString() + ' Day';
    if (durationDays > 1) { durationPart += 's'; }
    durationParts.push(durationPart);
  }
  return durationParts.join(' and ')
}

export function getMembershipStatusDisplay(status: MembershipStatus): string {
  if (status === MembershipStatus.draft) {
    return 'Draft';
  } else if (status === MembershipStatus.active) {
    return 'Active';
  } else if (status === MembershipStatus.retired) {
    return 'Retired';
  } else {
    return 'Unknown';
  }
}

export function getCustomerMembershipStatusDisplay(status: CustomerMembershipStatus): string {
  switch (status) {
    case CustomerMembershipStatus.awaitingInitialPayment:
      return 'Awaiting Initial Payment';
    case CustomerMembershipStatus.initialPaymentProcessing:
      return 'Initial Payment Processing';
    case CustomerMembershipStatus.initialPaymentCancelled:
      return 'Initial Payment Cancelled';
    case CustomerMembershipStatus.awaitingPaymentMethod:
      return 'Awaiting Payment Method';
    case CustomerMembershipStatus.deniedStaffApproval:
      return 'Denied Approval';
    case CustomerMembershipStatus.active:
      return 'Active';
    case CustomerMembershipStatus.frozen:
      return 'Frozen';
    case CustomerMembershipStatus.renewalPaymentPending:
      return 'Renewal Payment Pending';
    case CustomerMembershipStatus.renewalPaymentProcessing:
      return 'Renewal Payment Processing';
    case CustomerMembershipStatus.renewalPaymentFailed:
      return 'Renewal Payment Failed';
    case CustomerMembershipStatus.freezePaymentPending:
      return 'Freeze Payment Pending';
    case CustomerMembershipStatus.freezePaymentProcessing:
      return 'Freeze Payment Processing';
    case CustomerMembershipStatus.freezePaymentFailed:
      return 'Freeze Payment Failed';
    case CustomerMembershipStatus.ended:
      return 'Ended';
    default:
      return 'Unknown';
  }
}

/**
 * The text-color class for a membership status, matching the on-screen report
 * tables. Mirrors getClassForStatus(), which is duplicated across the members /
 * customer-memberships / roster-members components; getDisplayText reuses this
 * so the emailed report colors statuses identically.
 */
export function getMembershipStatusColorClass(status: CustomerMembershipStatus): string {
  switch (status) {
    case CustomerMembershipStatus.active:
      return 'text-success';
    case CustomerMembershipStatus.frozen:
    case CustomerMembershipStatus.ended:
    case CustomerMembershipStatus.deniedStaffApproval:
      return 'text-secondary';
    case CustomerMembershipStatus.renewalPaymentPending:
    case CustomerMembershipStatus.renewalPaymentFailed:
    case CustomerMembershipStatus.freezePaymentPending:
    case CustomerMembershipStatus.freezePaymentFailed:
      return 'text-danger';
    case CustomerMembershipStatus.initialPaymentProcessing:
    case CustomerMembershipStatus.renewalPaymentProcessing:
    case CustomerMembershipStatus.freezePaymentProcessing:
    // Paid for, but not active and not billable until someone acts (PRO-1460).
    case CustomerMembershipStatus.awaitingPaymentMethod:
      return 'text-warning';
    default:
      return '';
  }
}

/**
 * Whether a date falls outside the membership's customer cancellation date
 * range. Both bounds are inclusive; with no range (or no date) nothing is outside.
 */
export function isOutsideCancellationDateRange(membership: CustomerMembership, iso8601Date: string): boolean {
  const start = membership.membership_cancellation_date_range_start;
  const end = membership.membership_cancellation_date_range_end;
  if (!start || !end || !iso8601Date) {
    return false;
  }
  return iso8601Date < start || iso8601Date > end;
}

export function getMembershipDiscountApplyMethodDisplay(applyMethod: MembershipDiscountApplyMethod): string {
  switch (applyMethod) {
    case MembershipDiscountApplyMethod.code:
      return 'Code';
    case MembershipDiscountApplyMethod.automatic:
      return 'Automatic';
    case MembershipDiscountApplyMethod.referral:
      return 'Referral';
    case MembershipDiscountApplyMethod.cancellation:
      return 'Cancellation';
    case MembershipDiscountApplyMethod.bankTransfer:
      return 'Bank Transfer';
    case MembershipDiscountApplyMethod.freeze:
      return 'Freeze';
    default:
      return 'Unknown';
  }
}
